import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PaymentConfirmation from "./PaymentConfirmation";

import {
  CreditCard,
  User,
  MapPin,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Lock,
  Wallet,
  Mail,
  IdCard,
  Building2,
  GraduationCap,
} from "lucide-react";


/*
|--------------------------------------------------------------------------
| Initial Form
|--------------------------------------------------------------------------
*/

const initialForm = {
  /*
   * Student Information
   */
  matric_no: "",
  registration_no: "",
  name: "",
  department: "",
  programme: "",
  admission: "",

  /*
   * Billing Information
   */
  bill_to_forename: "",
  bill_to_surname: "",
  bill_to_email: "",

  /*
   * Payment
   */
  amount: "",

  /*
   * Address
   */
  bill_to_address_country: "BD",
  bill_to_address_line1: "",
  bill_to_address_city: "",
  bill_to_address_state: "",
  bill_to_address_postal_code: "",
};


/*
|--------------------------------------------------------------------------
| Student Payment
|--------------------------------------------------------------------------
*/

export default function StudentPayment() {

  const [step, setStep] = useState(1);

  const [form, setForm] =
    useState(initialForm);

  const [errors, setErrors] =
    useState({});


  /*
  |--------------------------------------------------------------------------
  | Generate Transaction UUID ONLY ONCE
  |--------------------------------------------------------------------------
  */

  const transactionUuid = useMemo(() => {

    if (
      typeof crypto !== "undefined" &&
      crypto.randomUUID
    ) {
      return crypto.randomUUID();
    }

    return (
      `${Date.now()}-` +
      Math.random()
        .toString(36)
        .substring(2, 12)
    );

  }, []);


  /*
  |--------------------------------------------------------------------------
  | Generate Reference Number ONLY ONCE
  |--------------------------------------------------------------------------
  */

  const referenceNumber = useMemo(() => {

    return (
      `${Date.now()}-` +
      Math.random()
        .toString(36)
        .substring(2, 8)
        .toUpperCase()
    );

  }, []);


  /*
  |--------------------------------------------------------------------------
  | Input Change
  |--------------------------------------------------------------------------
  */

  const handleChange = (e) => {

    const {
      name,
      value,
    } = e.target;


    let newValue = value;


    /*
    |--------------------------------------------------------------------------
    | Amount
    |--------------------------------------------------------------------------
    */

    if (name === "amount") {

      newValue =
        value.replace(
          /[^0-9.]/g,
          ""
        );


      /*
      | Prevent multiple decimal points
      */

      const parts =
        newValue.split(".");


      if (parts.length > 2) {

        newValue =
          `${parts[0]}.${parts
            .slice(1)
            .join("")}`;

      }

    }


    setForm((previous) => ({
      ...previous,
      [name]: newValue,
    }));


    /*
    |--------------------------------------------------------------------------
    | Remove field error
    |--------------------------------------------------------------------------
    */

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));

  };


  /*
  |--------------------------------------------------------------------------
  | Validate Step 1
  |--------------------------------------------------------------------------
  */

  const validateStepOne = () => {

    const requiredFields = [

      "matric_no",

      "registration_no",

      "name",

      "department",

      "programme",

      "admission",

      "bill_to_forename",

      "bill_to_surname",

      "bill_to_email",

      "amount",

      "bill_to_address_line1",

      "bill_to_address_city",

      "bill_to_address_state",

      "bill_to_address_postal_code",

    ];


    const newErrors = {};


    /*
    |--------------------------------------------------------------------------
    | Required fields
    |--------------------------------------------------------------------------
    */

    requiredFields.forEach((field) => {

      if (
        !String(
          form[field] || ""
        ).trim()
      ) {

        newErrors[field] =
          "This field is required";

      }

    });


    /*
    |--------------------------------------------------------------------------
    | Email
    |--------------------------------------------------------------------------
    */

    if (
      form.bill_to_email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        form.bill_to_email
      )
    ) {

      newErrors.bill_to_email =
        "Enter a valid email address";

    }


    /*
    |--------------------------------------------------------------------------
    | Amount
    |--------------------------------------------------------------------------
    */

    if (
      form.amount &&
      Number(form.amount) <= 0
    ) {

      newErrors.amount =
        "Enter a valid payment amount";

    }


    setErrors(
      newErrors
    );


    return (
      Object.keys(
        newErrors
      ).length === 0
    );

  };


  /*
  |--------------------------------------------------------------------------
  | Go To Step 2
  |--------------------------------------------------------------------------
  */

  const nextStep = () => {

    if (
      !validateStepOne()
    ) {
      return;
    }


    /*
    |--------------------------------------------------------------------------
    | Important:
    | form is directly passed to PaymentConfirmation.
    |--------------------------------------------------------------------------
    */

    setStep(2);


    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  };


  /*
  |--------------------------------------------------------------------------
  | Back To Step 1
  |--------------------------------------------------------------------------
  */

  const previousStep = () => {

    setStep(1);


    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  };


  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-6 sm:px-5 lg:px-8">

      <div className="mx-auto max-w-6xl">


        {/* ==========================================================
            STEPPER
        =========================================================== */}

        <div className="mb-6 flex items-center justify-center">

          <StepIndicator
            number={1}
            title="Student Information"
            active={step === 1}
            completed={step === 2}
          />


          <div
            className={`h-1 w-16 sm:w-28 lg:w-40 ${
              step === 2
                ? "bg-blue-600"
                : "bg-slate-200"
            }`}
          />


          <StepIndicator
            number={2}
            title="Review Payment"
            active={step === 2}
            completed={false}
          />

        </div>


        <AnimatePresence mode="wait">


          {/* ========================================================
              STEP 1
          ========================================================= */}

          {step === 1 && (

            <motion.div
              key="step-one"

              initial={{
                opacity: 0,
                x: -30,
              }}

              animate={{
                opacity: 1,
                x: 0,
              }}

              exit={{
                opacity: 0,
                x: 30,
              }}

              transition={{
                duration: 0.35,
              }}

              className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl"
            >


              {/* HEADER */}

              <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-violet-600 px-6 py-7 text-white sm:px-8">

                <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-white/10" />


                <div className="relative z-10 flex items-center gap-4">

                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/15 shadow-lg backdrop-blur">

                    <CreditCard
                      size={26}
                    />

                  </div>


                  <div>

                    <h1 className="text-xl font-bold sm:text-2xl">
                      Student Payment Information
                    </h1>

                    <p className="mt-1 text-sm text-blue-100">
                      Enter your information and payment amount
                    </p>

                  </div>

                </div>

              </div>


              {/* BODY */}

              <div className="p-5 sm:p-8">

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">


                  {/* ==================================================
                      STUDENT INFORMATION
                  =================================================== */}

                  <SectionCard
                    icon={<User size={18} />}
                    title="Student Information"
                  >

                    <InputField
                      label="Student ID / Matric No"
                      name="matric_no"
                      value={form.matric_no}
                      onChange={handleChange}
                      error={errors.matric_no}
                      icon={<IdCard size={16} />}
                      required
                    />


                    <InputField
                      label="Registration No"
                      name="registration_no"
                      value={form.registration_no}
                      onChange={handleChange}
                      error={errors.registration_no}
                      icon={<IdCard size={16} />}
                      required
                    />


                    <InputField
                      label="Student Name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      error={errors.name}
                      icon={<User size={16} />}
                      required
                    />


                    <InputField
                      label="Department"
                      name="department"
                      value={form.department}
                      onChange={handleChange}
                      error={errors.department}
                      icon={<Building2 size={16} />}
                      required
                    />


                    <InputField
                      label="Programme"
                      name="programme"
                      value={form.programme}
                      onChange={handleChange}
                      error={errors.programme}
                      icon={<GraduationCap size={16} />}
                      required
                    />


                    <InputField
                      label="Admission Semester"
                      name="admission"
                      value={form.admission}
                      onChange={handleChange}
                      error={errors.admission}
                      icon={<GraduationCap size={16} />}
                      required
                    />

                  </SectionCard>


                  {/* ==================================================
                      BILLING INFORMATION
                  =================================================== */}

                  <SectionCard
                    icon={<MapPin size={18} />}
                    title="Billing Information"
                  >


                    {/* AMOUNT */}

                    <div className="mb-5">

                      <label className="mb-2 block text-sm font-semibold text-slate-600">

                        Payment Amount

                        <span className="ml-1 text-red-500">
                          *
                        </span>

                      </label>


                      <div className="relative">

                        <span className="absolute left-4 top-1/2 z-10 -translate-y-1/2 font-bold text-green-600">
                          ৳
                        </span>


                        <input
                          type="text"
                          name="amount"
                          value={form.amount}
                          onChange={handleChange}
                          placeholder="0.00"

                          className={`h-12 w-full rounded-xl border bg-green-50 pl-10 pr-4 text-base font-bold text-green-700 outline-none transition focus:ring-4 focus:ring-green-500/10 ${
                            errors.amount
                              ? "border-red-400"
                              : "border-green-200 focus:border-green-500"
                          }`}
                        />

                      </div>


                      {errors.amount && (

                        <p className="mt-1 text-xs text-red-500">
                          {errors.amount}
                        </p>

                      )}

                    </div>


                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                      <InputField
                        label="First Name"
                        name="bill_to_forename"
                        value={form.bill_to_forename}
                        onChange={handleChange}
                        error={errors.bill_to_forename}
                        icon={<User size={16} />}
                        required
                      />


                      <InputField
                        label="Last Name"
                        name="bill_to_surname"
                        value={form.bill_to_surname}
                        onChange={handleChange}
                        error={errors.bill_to_surname}
                        icon={<User size={16} />}
                        required
                      />

                    </div>


                    <InputField
                      label="Email Address"
                      name="bill_to_email"
                      type="email"
                      value={form.bill_to_email}
                      onChange={handleChange}
                      error={errors.bill_to_email}
                      icon={<Mail size={16} />}
                      required
                    />


                    <InputField
                      label="Address"
                      name="bill_to_address_line1"
                      value={form.bill_to_address_line1}
                      onChange={handleChange}
                      error={errors.bill_to_address_line1}
                      icon={<MapPin size={16} />}
                      required
                    />


                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                      <InputField
                        label="City"
                        name="bill_to_address_city"
                        value={form.bill_to_address_city}
                        onChange={handleChange}
                        error={errors.bill_to_address_city}
                        required
                      />


                      <InputField
                        label="State"
                        name="bill_to_address_state"
                        value={form.bill_to_address_state}
                        onChange={handleChange}
                        error={errors.bill_to_address_state}
                        required
                      />

                    </div>


                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                      <InputField
                        label="Postal Code"
                        name="bill_to_address_postal_code"
                        value={form.bill_to_address_postal_code}
                        onChange={handleChange}
                        error={errors.bill_to_address_postal_code}
                        required
                      />


                      <InputField
                        label="Country"
                        name="bill_to_address_country"
                        value={form.bill_to_address_country}
                        onChange={handleChange}
                        required
                      />

                    </div>

                  </SectionCard>

                </div>


                {/* ==================================================
                    NEXT BUTTON
                =================================================== */}

                <div className="mt-8 flex justify-center border-t border-slate-200 pt-7">

                  <motion.button
                    type="button"
                    onClick={nextStep}

                    whileHover={{
                      y: -3,
                    }}

                    whileTap={{
                      scale: 0.97,
                    }}

                    className="flex h-12 min-w-[220px] items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-7 font-bold text-white shadow-lg shadow-blue-500/20 transition"
                  >

                    Continue to Review

                    <ArrowRight
                      size={18}
                    />

                  </motion.button>

                </div>

              </div>

            </motion.div>

          )}


          {/* ========================================================
              STEP 2
          ========================================================= */}

          {step === 2 && (

            <motion.div
              key="step-two"

              initial={{
                opacity: 0,
                x: 30,
              }}

              animate={{
                opacity: 1,
                x: 0,
              }}

              exit={{
                opacity: 0,
                x: -30,
              }}

              transition={{
                duration: 0.35,
              }}
            >

              {/*
              |--------------------------------------------------------------------------
              | IMPORTANT
              |
              | Directly pass the current `form` object.
              |
              | This fixes your screenshot problem.
              |--------------------------------------------------------------------------
              */}

              <PaymentConfirmation

                paymentData={form}

                transactionUuid={
                  transactionUuid
                }

                referenceNumber={
                  referenceNumber
                }

                onBack={
                  previousStep
                }

              />

            </motion.div>

          )}

        </AnimatePresence>

      </div>

    </div>
  );
}


/*
|--------------------------------------------------------------------------
| STEP INDICATOR
|--------------------------------------------------------------------------
*/

function StepIndicator({
  number,
  title,
  active,
  completed,
}) {

  return (

    <div className="flex items-center gap-2">

      <div
        className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-all ${
          active || completed
            ? "bg-blue-600 text-white shadow-lg shadow-blue-500/25"
            : "bg-slate-200 text-slate-500"
        }`}
      >

        {completed ? (

          <CheckCircle2
            size={19}
          />

        ) : (

          number

        )}

      </div>


      <span
        className={`hidden text-sm font-semibold sm:block ${
          active
            ? "text-blue-700"
            : "text-slate-500"
        }`}
      >
        {title}
      </span>

    </div>

  );
}


/*
|--------------------------------------------------------------------------
| SECTION CARD
|--------------------------------------------------------------------------
*/

function SectionCard({
  icon,
  title,
  children,
}) {

  return (

    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5 transition duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-6">

      <div className="mb-5 flex items-center gap-3 border-b border-slate-200 pb-4">

        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
          {icon}
        </div>

        <h3 className="font-bold text-slate-800">
          {title}
        </h3>

      </div>

      {children}

    </div>

  );
}


/*
|--------------------------------------------------------------------------
| INPUT FIELD
|--------------------------------------------------------------------------
*/

function InputField({
  label,
  name,
  type = "text",
  value,
  onChange,
  error,
  icon,
  required = false,
}) {

  return (

    <div className="mb-4 last:mb-0">

      <label
        htmlFor={name}
        className="mb-2 block text-sm font-semibold text-slate-600"
      >

        {label}

        {required && (

          <span className="ml-1 text-red-500">
            *
          </span>

        )}

      </label>


      <div className="relative">

        {icon && (

          <span className="absolute left-3 top-1/2 z-10 -translate-y-1/2 text-slate-400">
            {icon}
          </span>

        )}


        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          autoComplete="off"

          className={`h-12 w-full rounded-xl border bg-white ${
            icon
              ? "pl-10"
              : "px-4"
          } pr-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:-translate-y-[1px] focus:ring-4 ${
            error
              ? "border-red-400 focus:border-red-500 focus:ring-red-500/10"
              : "border-slate-200 focus:border-blue-500 focus:ring-blue-500/10"
          }`}
        />

      </div>


      {error && (

        <p className="mt-1 text-xs font-medium text-red-500">
          {error}
        </p>

      )}

    </div>

  );
}